import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { PaginationResponse } from '@/types';
import { cn } from '@/utils/cn';

const DEFAULT_LIMIT = 20;

export type ServerSearchSelectProps<T extends { id: number }> = {
  id?: string;
  value: number | null | undefined;
  /** Rótulo exibido quando há seleção (controlado pelo pai em modo edição). */
  selectedLabel?: string;
  onChange: (id: number | null, item: T | null) => void;
  fetchPage: (params: {
    page: number;
    limit: number;
    search?: string;
  }) => Promise<PaginationResponse<T>>;
  /** Opcional: carrega o item selecionado se não estiver na página atual. */
  fetchById?: (id: number) => Promise<T>;
  getOptionLabel: (item: T) => string;
  queryKey: string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  emptyMessage?: string;
};

/**
 * Select com busca server-side (paginação limitada).
 * HF-6: substitui carregamentos limit 1000/10000 + filtro no client.
 */
export function ServerSearchSelect<T extends { id: number }>({
  id,
  value,
  selectedLabel,
  onChange,
  fetchPage,
  fetchById,
  getOptionLabel,
  queryKey,
  placeholder = 'Buscar…',
  error,
  disabled,
  allowClear = true,
  className,
  emptyMessage = 'Nenhum resultado',
}: ServerSearchSelectProps<T>) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selectedLabel ?? '');
  const debouncedQuery = useDebouncedValue(query, 300);

  useEffect(() => {
    if (selectedLabel !== undefined) {
      setQuery(selectedLabel);
    }
  }, [selectedLabel]);

  const searchParam = open ? (debouncedQuery.trim() || undefined) : undefined;

  const { data, isFetching } = useQuery({
    queryKey: [queryKey, 'server-search', { search: searchParam, open }],
    queryFn: () =>
      fetchPage({
        page: 1,
        limit: DEFAULT_LIMIT,
        search: searchParam,
      }),
    enabled: open && !disabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const { data: selectedItem } = useQuery({
    queryKey: [queryKey, 'selected', value],
    queryFn: () => fetchById!(Number(value)),
    enabled: Boolean(fetchById && value && value > 0 && !selectedLabel),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (selectedItem && !selectedLabel) {
      setQuery(getOptionLabel(selectedItem));
    }
  }, [selectedItem, selectedLabel, getOptionLabel]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;
      if (containerRef.current && target && !containerRef.current.contains(target)) {
        setOpen(false);
        if (value && selectedLabel) {
          setQuery(selectedLabel);
        } else if (value && selectedItem) {
          setQuery(getOptionLabel(selectedItem));
        } else if (!value) {
          setQuery('');
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, selectedLabel, selectedItem, getOptionLabel]);

  const options = data?.data ?? [];

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <input
        id={inputId}
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        className={cn(
          'w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500',
          error ? 'border-error-500' : 'border-gray-300'
        )}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) {
            onChange(null, null);
          }
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      {allowClear && query && !disabled && (
        <button
          type="button"
          aria-label="Limpar seleção"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          onClick={() => {
            setQuery('');
            onChange(null, null);
            setOpen(false);
          }}
        >
          ×
        </button>
      )}
      {open && !disabled && (
        <div
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
          onMouseDown={(e) => e.stopPropagation()}
          role="listbox"
        >
          {isFetching && (
            <div className="px-3 py-2 text-sm text-gray-500">Buscando…</div>
          )}
          {!isFetching &&
            options.map((item: T) => (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={value === item.id}
                className={cn(
                  'w-full text-left px-3 py-2 hover:bg-gray-100',
                  value === item.id && 'bg-primary-50'
                )}
                onClick={() => {
                  onChange(item.id, item);
                  setQuery(getOptionLabel(item));
                  setOpen(false);
                }}
              >
                {getOptionLabel(item)}
              </button>
            ))}
          {!isFetching && options.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</div>
          )}
        </div>
      )}
    </div>
  );
}
